/* eslint-disable fp/no-loops */
/* eslint-disable fp/no-mutating-methods */
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'

import { BootcampBossData } from '~/bootcamp/types/bootcamp.types'
import { Activity } from '~/bootcamp/components/Quiz/QuizData.models'

import { bootcampSanity } from '~/shared/sanity/bootcamp'

import { QuizQuestionConfigProp } from '~/bootcamp/components/Quiz/QuizProps.models'

import { bootcampSplitApi } from './split.api'

const getDifficultyLevelAndActivityMap = (bossData: BootcampBossData[]): { [key: number]: Activity[] } => {
  // a function to map all the activities related to each difficulty levels

  const dl_activity_map: { [key: number]: Activity[] } = {}
  bossData.forEach((level) => {
    // For each levels select activities
    level.activities.forEach((activity) => {
      // For each activities select difficuly levels
      activity.difficulties.forEach(
        ({
          difficultyLevel,
          activityName,
          keyboardLayout,
          description,
          questionType,
          multipleChoiceActivity,
          fillInTheBlankActivity,
          circleCombinationsActivity,
          categorizationActivity,
          matchingPairsActivity,
          orderElementsActivity,
          numberLineActivity,
          measureTheItemActivity,
        }) => {
          // For each difficulty levels store the related level and activity in hash
          if (dl_activity_map[difficultyLevel] !== undefined) {
            dl_activity_map[difficultyLevel].push({
              name: activityName,
              level: +level.level,
              difficultyLevel,
              keyboardLayout,
              type: activity.activityType,
              activityWeight: activity.activityWeight,
              description,
              questionType,
              multipleChoiceActivity,
              fillInTheBlankActivity,
              circleCombinationsActivity,
              categorizationActivity,
              matchingPairsActivity,
              orderElementsActivity,
              numberLineActivity,
              measureTheItemActivity,
            })
          } else {
            dl_activity_map[difficultyLevel] = [
              {
                name: activityName,
                level: +level.level,
                difficultyLevel,
                keyboardLayout,
                type: activity.activityType,
                activityWeight: activity.activityWeight,
                description,
                questionType,
                multipleChoiceActivity,
                fillInTheBlankActivity,
                circleCombinationsActivity,
                categorizationActivity,
                matchingPairsActivity,
                orderElementsActivity,
                numberLineActivity,
                measureTheItemActivity,
              },
            ]
          }
        },
      )
    })
  })
  return dl_activity_map
}

const getActivitiesWithEqualProbability = (bossData: BootcampBossData[], total: number, desc?: boolean): Activity[] => {
  if (!bossData || !total) return []

  let difficultyLevels = Array.from(
    new Set(bossData.flatMap((l) => l.activities.flatMap((a) => a.difficulties.map((d) => d.difficultyLevel)))),
  ).sort() // Get all the unique difficulty level and sort them ascending
  difficultyLevels = desc ? difficultyLevels.reverse() : difficultyLevels // if desc is true, reverse it

  const dl_activity_map = getDifficultyLevelAndActivityMap(bossData)

  const pickedActivities = new Set() // Set to keep track of choosen activities
  const result: Activity[] = []

  while (total > result.length) {
    difficultyLevels.forEach((dl) => {
      const available = dl_activity_map[dl].filter((act) => !pickedActivities.has(`${act.level}-${act.type}`))
      if (available.length) {
        const selected = available[Math.floor(Math.random() * available.length)]
        result.push({ ...selected })
        pickedActivities.add(`${selected.level}-${selected.type}`)
      }
    })
  }
  return result
}

const getQuestionConfigFromBootcampBossData = (bossData: BootcampBossData[] | null): Activity[] => {
  if (!bossData) return []
  const totalQuestions = bossData
    .flatMap((level) => level.activities.filter((a) => a.difficulties.length).length)
    .reduce((p, c) => p + c, 0) // find total number of activities
  const questions = getActivitiesWithEqualProbability(bossData, totalQuestions, true)
  return questions.sort((a, b) =>
    a.activityWeight === b.activityWeight ? a.difficultyLevel - b.difficultyLevel : a.activityWeight - b.activityWeight,
  )
}

const GetBoss = bootcampSplitApi.injectEndpoints({
  endpoints: (build) => ({
    getBootcampBoss: build.query<QuizQuestionConfigProp | null, { language: string; key: string; module: string }>({
      queryFn: async (args) => {
        try {
          const rawData = await bootcampSanity.getBootcampBossData(args)
          const questions = getQuestionConfigFromBootcampBossData(rawData.levels)
          const result: QuizQuestionConfigProp = {
            questions: questions ? questions : [],
            // the max wrong answers you can have is one per every 5 questions but it allows 0 so we subtract 1
            maxWrongAnswer: questions.length > 5 ? Math.floor(questions.length / 5) : 0,
            badgeImage: rawData.badgeImage ?? { alt: '', srcKey: '' },
            taskId: rawData.taskId ?? '',
            bossBattleEnemy: rawData.bossBattleEnemy,
          }
          return { data: result }
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          }
        }
      },
      providesTags: ['Boss'],
    }),
  }),
  overrideExisting: false,
})

export const { useGetBootcampBossQuery } = GetBoss
